/*
 * BLT Cart controller — one thin delegated controller for the drawer AND the cart page.
 *
 * Design principle: every mutation re-renders the affected cart sections through Shopify's
 * Section Rendering API (the `sections` param on /cart/change.js, /cart/update.js, /cart/add.js).
 * All money / reward math lives in Liquid, so this file only fetches, swaps the rendered HTML
 * into each live [data-blt-cart-root], keeps the header count in sync, and drives the drawer.
 *
 * Roots: any element with [data-blt-cart-root] carrying:
 *   data-section       — the section id to request from Section Rendering
 *   data-context       — 'drawer' | 'page'
 *   data-cart-count    — cart.item_count at render time
 *   data-applied-codes — comma-joined applicable discount codes at render time
 *   data-edd-days      — estimated-delivery offset in days (theme setting)
 *
 * Cart v2 additions (Figma 2763:118):
 *   - kit adds carry the hidden line property `_blt_source: kit`, which switches
 *     that line to the compact row design in blt-cart-line-items.liquid
 *   - inline size dropdown on full line items -> swapVariant (add new + remove old)
 *   - collapsible coupon with green chip / red invalid states
 *   - estimated-delivery line painted client-side (order date + N days)
 *   - sticky TOTAL/checkout bar shown while the inline CTA is out of view
 *   - stock guard (Figma 2763:639): re-checks live availability on open / tab
 *     refocus / checkout tap via sections/blt-cart-stock-data.liquid; a stale
 *     sold-out line gets a banner + red "select size" chip and checkout blocks
 *     until resolved. The checkout-tap check FAILS OPEN (a network hiccup must
 *     never block buying).
 *   - reviews strip aggregate (Spectrum app proxy, cached, fail-silent)
 *
 * No ES module imports — loaded once globally via <script defer>.
 */
(function () {
  'use strict';

  var drawer = document.querySelector('[data-blt-cart]'); // the global drawer shell
  var pending = false;

  /* -------- helpers -------- */
  function roots() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-blt-cart-root]'));
  }
  function sectionParam() {
    var ids = roots()
      .map(function (r) { return r.getAttribute('data-section'); })
      .filter(function (id, i, a) { return id && a.indexOf(id) === i; });
    return ids.join(',');
  }
  function cartCount() {
    var r = roots()[0];
    return parseInt((r && r.getAttribute('data-cart-count')) || '0', 10) || 0;
  }
  function appliedCodes() {
    // authoritative list of currently-applicable codes (from any root, they match)
    var r = roots()[0];
    var raw = (r && r.getAttribute('data-applied-codes')) || '';
    return raw.split(',').map(function (c) { return c.trim(); }).filter(Boolean);
  }
  function unique(arr) {
    var seen = {}, out = [];
    arr.forEach(function (c) {
      var k = c.toLowerCase();
      if (!seen[k]) { seen[k] = 1; out.push(c); }
    });
    return out;
  }
  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); });
  }
  function baseRoot() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  /* -------- render swapping -------- */
  function swapSections(sections) {
    if (!sections) return;
    Object.keys(sections).forEach(function (id) {
      var doc = new DOMParser().parseFromString(sections[id], 'text/html');
      var fresh =
        doc.querySelector('[data-blt-cart-root][data-section="' + id + '"]') ||
        doc.querySelector('[data-blt-cart-root]');
      if (!fresh) return;
      document
        .querySelectorAll('[data-blt-cart-root][data-section="' + id + '"]')
        .forEach(function (target) {
          target.innerHTML = fresh.innerHTML;
          ['data-cart-count', 'data-applied-codes'].forEach(function (attr) {
            if (fresh.hasAttribute(attr)) target.setAttribute(attr, fresh.getAttribute(attr));
          });
        });
    });
    afterRender();
  }

  // everything that must re-run whenever a root's DOM is replaced
  function afterRender() {
    syncHeader();
    paintEdd();
    bindSticky();
    reopenCoupon();
    if (guardBadKeys.length) applyStockUI();
  }

  function syncHeader() {
    var r = roots()[0];
    if (!r) return;
    var n = cartCount();
    document.querySelectorAll('.blt-header__cart').forEach(function (a) {
      a.textContent = n > 0 ? 'cart (' + n + ')' : 'cart';
    });
    document.querySelectorAll('[data-cart-count-badge]').forEach(function (el) {
      el.textContent = n;
      el.hidden = n < 1;
    });
  }

  function dispatchCartUpdate() {
    document.dispatchEvent(
      new CustomEvent('cart:update', { bubbles: true, detail: { data: { source: 'blt-cart' } } })
    );
  }

  /* -------- estimated delivery (Figma 2763:134, minus the pincode) --------
     "delivered by mon, 15 sep" = order date + N days. N comes from the
     cart root's data-edd-days (theme setting `cart_edd_days`), so the client
     changes it in the editor — no code edit, nothing hardcoded. 0 disables. */
  var EDD_DAYS_NAMES = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  var EDD_MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  function paintEdd() {
    roots().forEach(function (root) {
      var days = parseInt(root.getAttribute('data-edd-days') || '4', 10);
      root.querySelectorAll('[data-blt-edd]').forEach(function (el) {
        if (isNaN(days) || days <= 0) { el.hidden = true; return; }
        var d = new Date(Date.now() + days * 86400000);
        el.textContent = 'delivered by ' + EDD_DAYS_NAMES[d.getDay()] + ', ' + d.getDate() + ' ' + EDD_MONTHS[d.getMonth()];
        el.hidden = false;
      });
    });
  }

  /* -------- sticky TOTAL / checkout bar -------- */
  function bindSticky() {
    roots().forEach(function (root) {
      var sticky = root.querySelector('[data-blt-sticky]');
      var actions = root.querySelector('[data-blt-actions]');
      if (root.__bltStickyIO) { root.__bltStickyIO.disconnect(); root.__bltStickyIO = null; }
      if (!sticky || !actions || !('IntersectionObserver' in window)) return;
      var scrollEl = root.querySelector('.blt-cart__scroll'); // drawer scroll container; null on the page (viewport)
      var io = new IntersectionObserver(function (entries) {
        sticky.hidden = entries[0].isIntersecting;
      }, { root: scrollEl || null, threshold: 0.05 });
      io.observe(actions);
      root.__bltStickyIO = io;
    });
  }

  /* Reviews strip is now fully server-rendered in blt-cart-reviews.liquid
     from Spectrum's `spectrum.review_stats` product metafields — no fetch,
     no cache; Section Rendering re-renders it with the rest of the cart. */

  /* -------- kit size picker (popup before add for sized products) --------
     Built once, appended to <body> so it overlays the drawer and survives
     section re-renders. Sizes come from each row's data-kit-variants JSON
     (rendered from the product's variants in Liquid — nothing hardcoded). */
  var kitDlg = null, kitDlgCtx = 'drawer', kitDlgReturn = null;
  function ensureKitDialog() {
    if (kitDlg) return kitDlg;
    kitDlg = document.createElement('div');
    kitDlg.className = 'blt-kit-size';
    kitDlg.setAttribute('aria-hidden', 'true');
    kitDlg.innerHTML =
      '<div class="blt-kit-size__overlay" data-kit-size-close></div>' +
      '<div class="blt-kit-size__panel" role="dialog" aria-modal="true" aria-labelledby="blt-kit-size-title">' +
        '<button type="button" class="blt-kit-size__close" data-kit-size-close aria-label="Close">' +
          '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>' +
        '</button>' +
        '<p class="blt-kit-size__label" data-kit-size-label></p>' +
        '<h3 class="blt-kit-size__title" id="blt-kit-size-title" data-kit-size-title></h3>' +
        '<div class="blt-kit-size__grid" data-kit-size-grid></div>' +
        '<button type="button" class="blt-kit-size__atc" data-kit-size-atc disabled>add to cart</button>' +
      '</div>';
    document.body.appendChild(kitDlg);
    return kitDlg;
  }
  function kitDialogOpen() {
    return !!(kitDlg && kitDlg.classList.contains('is-open'));
  }
  function openKitDialog(btn, ctx) {
    var variants = null;
    try { variants = JSON.parse(btn.getAttribute('data-kit-variants') || ''); } catch (err) { /* fall through */ }
    if (!variants || !variants.length) {
      addKit(btn.getAttribute('data-kit-variant'), btn, ctx); // degrade to the old direct add
      return;
    }
    var dlg = ensureKitDialog();
    kitDlgCtx = ctx;
    kitDlgReturn = btn;
    var optName = (btn.getAttribute('data-kit-option') || 'size').toLowerCase();
    dlg.querySelector('[data-kit-size-label]').textContent = 'select ' + optName;
    dlg.querySelector('[data-kit-size-title]').textContent = btn.getAttribute('data-kit-title') || '';
    var grid = dlg.querySelector('[data-kit-size-grid]');
    grid.innerHTML = '';
    variants.forEach(function (v) {
      var cell = document.createElement('button');
      cell.type = 'button';
      cell.className = 'blt-kit-size__cell';
      cell.textContent = v.label;
      cell.setAttribute('data-kit-size-cell', '');
      cell.setAttribute('data-variant-id', v.id);
      if (!v.available) { cell.disabled = true; cell.setAttribute('aria-disabled', 'true'); }
      grid.appendChild(cell);
    });
    var atc = dlg.querySelector('[data-kit-size-atc]');
    atc.disabled = true;
    atc.textContent = 'add to cart';
    atc.removeAttribute('data-selected-variant');
    atc.setAttribute('data-kit-handle', btn.getAttribute('data-kit-handle') || '');
    dlg.classList.add('is-open');
    dlg.setAttribute('aria-hidden', 'false');
    var first = grid.querySelector('button:not([disabled])');
    if (first) requestAnimationFrame(function () { first.focus(); });
  }
  function closeKitDialog() {
    if (!kitDialogOpen()) return;
    kitDlg.classList.remove('is-open');
    kitDlg.setAttribute('aria-hidden', 'true');
    if (kitDlgReturn && document.contains(kitDlgReturn)) kitDlgReturn.focus();
    kitDlgReturn = null;
  }

  /* -------- drawer open / close -------- */
  var lastFocused = null;
  function focusables() {
    var panel = drawer && drawer.querySelector('.blt-cart__panel');
    if (!panel) return [];
    return Array.prototype.slice
      .call(panel.querySelectorAll('a[href],button:not([disabled]),input,select,textarea,[tabindex]:not([tabindex="-1"])'))
      .filter(function (el) { return el.offsetParent !== null; });
  }
  function openDrawer() {
    if (!drawer || drawer.classList.contains('is-open')) return;
    lastFocused = document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('blt-cart-locked');
    if (window.BltScrollLock) BltScrollLock.lock(); // iOS-safe background lock
    var close = drawer.querySelector('.blt-cart__back');
    if (close) requestAnimationFrame(function () { close.focus(); });
    validateStockThrottled();
  }
  function closeDrawer() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('blt-cart-locked');
    if (window.BltScrollLock) BltScrollLock.unlock();
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    lastFocused = null;
  }
  function refreshDrawer(openAfter) {
    var root = drawer && drawer.querySelector('[data-blt-cart-root]');
    if (!root) { if (openAfter) openDrawer(); return Promise.resolve(); }
    var id = root.getAttribute('data-section');
    return fetch(baseRoot() + '?section_id=' + id)
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var obj = {}; obj[id] = html;
        swapSections(obj);
        if (openAfter) openDrawer();
      })
      .catch(function () { if (openAfter) openDrawer(); });
  }
  // refresh EVERY cart root (drawer + page) — used when live data may have drifted
  function refreshAllRoots() {
    var ids = sectionParam().split(',').filter(Boolean);
    return Promise.all(ids.map(function (id) {
      return fetch(baseRoot() + '?section_id=' + id)
        .then(function (r) { return r.text(); })
        .then(function (html) { var obj = {}; obj[id] = html; swapSections(obj); })
        .catch(function () {});
    }));
  }

  /* -------- mutations -------- */
  function setBusy(el, on) { if (el) el.classList.toggle('is-busy', !!on); }

  /* This store's /cart/change.js is UNRELIABLE when a line is addressed by its
     line key (`id`): a Spectrum cart-transform reissues line keys, so change.js
     can split a variant into two lines instead of updating the existing one
     (proven: change {id:key, quantity:2} on a 2-item cart -> duplicate line;
     change {line:N, quantity:2} -> correct). So resolve the CURRENT 1-based
     line index from a fresh /cart.js by key at click time and address by
     `line`. Bulletproof against key drift, and self-heals if the row is
     already gone (returns 0 -> re-render). */
  function resolveLine(key) {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        for (var i = 0; i < cart.items.length; i++) {
          if (cart.items[i].key === key) return i + 1;
        }
        return 0;
      });
  }

  function change(key, qty, li) {
    if (pending) return;
    pending = true;
    setBusy(li, true);
    resolveLine(key)
      .then(function (line) {
        if (!line) { refreshAllRoots(); return null; } // line already gone — resync
        return postJSON('/cart/change.js', {
          line: line,
          quantity: qty,
          sections: sectionParam(),
          sections_url: window.location.pathname
        });
      })
      .then(function (cart) {
        if (!cart || cart.status) return; // no-op or error — leave as-is
        swapSections(cart.sections);
        dispatchCartUpdate();
        if (guardBadKeys.length) validateStock(false);
        return healSplitLines(cart); // awaited so pending stays held during heal
      })
      .catch(function () { refreshAllRoots(); })
      .then(function () { pending = false; setBusy(li, false); });
  }

  /* Store-level guard: this shop runs a Spectrum Cart Transform (Shopify
     Function, app-owned) that re-runs on /cart/change.js and can SPLIT a line
     into two identical lines (same variant, same empty properties, different
     keys) when a quantity increase is requested — reproducible on the live
     theme too, so it is a pre-existing store behaviour, not this cart. We can't
     touch the transform, and neither change.js nor add.js can cleanly raise the
     quantity past what the transform allows. So we never let the cart DISPLAY a
     corrupted duplicate: if a variant+properties signature appears on more than
     one line, collapse the extras with a quantity:0 removal (which the transform
     does NOT split) and re-render. Net effect: the item stays at its allowed
     quantity instead of showing phantom duplicate rows. */
  function lineSig(item) {
    return item.variant_id + '|' + JSON.stringify(item.properties || {});
  }
  function healSplitLines(cart) {
    if (!cart || !Array.isArray(cart.items)) return Promise.resolve();
    var seen = {}, dupeKeys = [];
    cart.items.forEach(function (it) {
      var s = lineSig(it);
      if (seen[s]) dupeKeys.push(it.key); else seen[s] = true;
    });
    if (!dupeKeys.length) return Promise.resolve();
    // remove each duplicate line by its current index, re-reading between ops so
    // indices stay valid as the cart shrinks; then repaint from the final cart.
    return dupeKeys.reduce(function (chain, key) {
      return chain.then(function () {
        return resolveLine(key).then(function (line) {
          if (!line) return null;
          return postJSON('/cart/change.js', { line: line, quantity: 0 });
        });
      });
    }, Promise.resolve()).then(function () { return refreshAllRoots(); });
  }

  function addKit(variantId, btn, ctx, onSuccess) {
    if (pending || !variantId) return;
    pending = true;
    setBusy(btn, true);
    postJSON('/cart/add.js', {
      // the hidden property marks "added from the kit" — it drives the compact
      // line-item design and survives reloads (it's real cart data)
      items: [{ id: parseInt(variantId, 10), quantity: 1, properties: { _blt_source: 'kit' } }],
      sections: sectionParam(),
      sections_url: window.location.pathname
    })
      .then(function (res) {
        if (res && res.status) return; // add error (e.g. sold out) — leave UI as-is
        var handle = btn && btn.getAttribute('data-kit-handle');
        if (handle) {
          document.dispatchEvent(new CustomEvent('blt:kit-added', { detail: { handle: handle } }));
        }
        swapSections(res.sections);
        if (ctx !== 'page') openDrawer(); // on the cart page, don't pop the drawer over the page
        dispatchCartUpdate();
        if (onSuccess) onSuccess();
      })
      .catch(function () {})
      .then(function () { pending = false; setBusy(btn, false); });
  }

  /* -------- inline size change (full line items) --------
     /cart/change.js can't move a line to another variant, so a size change is
     add-the-new + remove-the-old (in that order — the cart never dips through
     a lower free-shipping tier or looks empty mid-swap). Line properties are
     carried over, so a kit line keeps its compact design. If the new size is
     already a line, Shopify merges quantities — the re-render reflects it. */
  function swapVariant(li, newVid) {
    if (pending) return;
    var key = li.getAttribute('data-line-key');
    var qtyEl = li.querySelector('[data-qty]');
    var qty = parseInt(qtyEl ? qtyEl.textContent : '1', 10) || 1;
    var props = null;
    try { props = JSON.parse(li.getAttribute('data-line-props') || 'null'); } catch (e) { /* ignore */ }
    var item = { id: parseInt(newVid, 10), quantity: qty };
    if (props && typeof props === 'object' && Object.keys(props).length) item.properties = props;
    pending = true;
    setBusy(li, true);
    postJSON('/cart/add.js', { items: [item] })
      .then(function (res) {
        if (res && res.status) throw new Error('add failed'); // e.g. not enough stock
        return resolveLine(key); // remove-the-old by line index (key-addressing is unreliable here)
      })
      .then(function (line) {
        if (!line) return null; // old line already gone (e.g. merged) — nothing to remove
        return postJSON('/cart/change.js', {
          line: line,
          quantity: 0,
          sections: sectionParam(),
          sections_url: window.location.pathname
        });
      })
      .then(function (cart) {
        if (cart && cart.status) throw new Error('remove failed');
        if (cart && cart.sections) swapSections(cart.sections);
        else refreshAllRoots();
        dispatchCartUpdate();
        if (guardBadKeys.length) validateStock(false);
      })
      .catch(function () {
        // reality may now hold both lines — re-render to whatever the cart truly is
        refreshAllRoots();
      })
      .then(function () { pending = false; setBusy(li, false); });
  }
  function closeSizeMenus(except) {
    document.querySelectorAll('[data-size-picker] .blt-cr-size__menu:not([hidden])').forEach(function (menu) {
      if (except && menu.closest('[data-size-picker]') === except) return;
      menu.hidden = true;
      var t = menu.closest('[data-size-picker]').querySelector('[data-size-toggle]');
      if (t) t.setAttribute('aria-expanded', 'false');
    });
  }

  /* -------- stock guard (Figma 2763:639) --------
     Live availability check via sections/blt-cart-stock-data.liquid. A line
     whose variant sold out while it sat in the cart gets a banner + red
     "select size" chip; checkout blocks until the shopper re-picks or removes.
     Never silently removes a line, never auto-swaps. */
  var guardBadKeys = [];
  var guardBadLines = [];
  var lastStockCheck = 0;
  function fetchStock() {
    return fetch(baseRoot() + '?section_id=blt-cart-stock-data', { headers: { Accept: 'text/html' } })
      .then(function (r) { if (!r.ok) throw new Error('http ' + r.status); return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var node = doc.querySelector('[data-blt-stock-json]');
        if (!node) throw new Error('payload missing');
        var data = JSON.parse(node.textContent);
        if (!data || data.v !== 1 || !Array.isArray(data.lines)) throw new Error('bad payload');
        return data.lines;
      });
  }
  function validateStock(needFreshRender) {
    lastStockCheck = Date.now();
    return fetchStock()
      .then(function (lines) {
        var bad = lines.filter(function (l) { return l.available === false; });
        var hadBad = guardBadKeys.length > 0;
        guardBadLines = bad;
        guardBadKeys = bad.map(function (l) { return l.key; });
        if (bad.length && needFreshRender) {
          // re-render first so the size dropdowns hold CURRENT availability
          return refreshAllRoots().then(function () { applyStockUI(); return false; });
        }
        if (bad.length || hadBad) applyStockUI();
        return bad.length === 0;
      })
      .catch(function () { return true; }); // fail open — a hiccup must never block buying
  }
  function validateStockThrottled() {
    if (cartCount() < 1) return;
    if (Date.now() - lastStockCheck < 10000) return;
    validateStock(true);
  }
  function applyStockUI() {
    document.querySelectorAll('[data-stock-banner]').forEach(function (b) { b.remove(); });
    roots().forEach(function (root) {
      root.querySelectorAll('.blt-cr-item.is-stocked-out').forEach(function (li) {
        li.classList.remove('is-stocked-out');
      });
      var firstBad = null;
      guardBadLines.forEach(function (l) {
        var li = root.querySelector('.blt-cr-item[data-line-key="' + CSS.escape(l.key) + '"]');
        if (!li) return;
        li.classList.add('is-stocked-out');
        var chipLabel = li.querySelector('[data-size-chip-label]');
        if (chipLabel && l.sized) chipLabel.textContent = 'select size';
        if (!firstBad) firstBad = { li: li, line: l };
      });
      if (firstBad) {
        var banner = document.createElement('li');
        banner.className = 'blt-cr-stockbanner';
        banner.setAttribute('data-stock-banner', '');
        banner.setAttribute('role', 'alert');
        var title = firstBad.line.sized
          ? firstBad.line.label + ' just sold out'
          : firstBad.line.title + ' just sold out';
        var sub = firstBad.line.sized
          ? 'pick another size to keep this in your cart.'
          : 'remove it to keep the rest of your cart moving.';
        banner.innerHTML =
          '<p class="blt-cr-stockbanner__title"></p><p class="blt-cr-stockbanner__sub"></p>';
        banner.querySelector('.blt-cr-stockbanner__title').textContent = title;
        banner.querySelector('.blt-cr-stockbanner__sub').textContent = sub;
        firstBad.li.parentElement.insertBefore(banner, firstBad.li);
      }
    });
    var blocked = guardBadKeys.length > 0;
    document.querySelectorAll('[data-checkout-btn]').forEach(function (btn) {
      btn.classList.toggle('is-disabled', blocked);
      btn.setAttribute('aria-disabled', blocked ? 'true' : 'false');
    });
  }
  /* On checkout tap: when the guard already knows about a dead line, block the
     click (banner explains why). Otherwise DON'T intercept — the click passes
     through natively so Razorpay Magic's own checkout hook behaves exactly as
     before; we just fire a background re-check so a drift is caught for next
     time. Fail-open by construction. */
  function checkoutTap(e) {
    if (guardBadKeys.length) {
      e.preventDefault();
      var banner = document.querySelector('[data-stock-banner]');
      if (banner) banner.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    validateStock(false);
  }
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState !== 'visible') return;
    var onCartPage = roots().some(function (r) { return r.getAttribute('data-context') === 'page'; });
    var drawerOpen = drawer && drawer.classList.contains('is-open');
    if (onCartPage || drawerOpen) validateStockThrottled();
  });

  /* -------- coupon -------- */
  var couponOpen = false;
  function reopenCoupon() {
    if (!couponOpen) return;
    document.querySelectorAll('[data-coupon-wrap]').forEach(function (wrap) {
      var form = wrap.querySelector('[data-coupon-form]');
      var toggle = wrap.querySelector('[data-coupon-toggle]');
      if (form) form.hidden = false;
      if (toggle) toggle.hidden = true;
    });
  }
  function submitCodes(codes, form, newCode) {
    if (pending) return;
    pending = true;
    if (form) form.classList.add('is-busy');
    postJSON('/cart/update.js', {
      discount: codes.join(','),
      sections: sectionParam(),
      sections_url: window.location.pathname
    })
      .then(function (cart) {
        var invalid = false;
        if (newCode) {
          var dc = (cart.discount_codes || []).filter(function (x) {
            return x.code && x.code.toLowerCase() === newCode.toLowerCase();
          })[0];
          invalid = !!(dc && dc.applicable === false);
        }
        if (newCode && !invalid) couponOpen = false; // chip takes over
        swapSections(cart.sections);
        dispatchCartUpdate();
        if (invalid) {
          showCouponError("that code isn’t valid on this cart", newCode);
        } else {
          clearCouponError();
        }
      })
      .catch(function () {})
      .then(function () {
        pending = false;
        if (form) form.classList.remove('is-busy');
      });
  }
  function applyCode(code, form) {
    submitCodes(unique(appliedCodes().concat([code])), form, code);
  }
  function removeCode(code) {
    var next = appliedCodes().filter(function (c) { return c.toLowerCase() !== code.toLowerCase(); });
    submitCodes(next, null, null);
  }
  function showCouponError(msg, keepCode) {
    document.querySelectorAll('[data-coupon-error]').forEach(function (el) {
      el.textContent = msg; el.hidden = false;
    });
    document.querySelectorAll('[data-coupon-form]').forEach(function (f) {
      f.classList.add('is-invalid');
      var input = f.querySelector('[data-coupon-input]');
      if (input && keepCode && !input.value) input.value = keepCode;
    });
  }
  function clearCouponError() {
    document.querySelectorAll('[data-coupon-error]').forEach(function (el) {
      el.textContent = ''; el.hidden = true;
    });
    document.querySelectorAll('[data-coupon-form]').forEach(function (f) {
      f.classList.remove('is-invalid');
    });
  }

  /* -------- delegated events -------- */
  document.addEventListener('click', function (e) {
    var openBtn = e.target.closest('[data-blt-cart-open]');
    if (openBtn) { e.preventDefault(); openDrawer(); refreshDrawer(false); return; }
    if (e.target.closest('[data-blt-cart-close]')) { closeDrawer(); return; }

    // size-picker dialog lives on <body>, outside any cart root — handle it first
    if (kitDialogOpen()) {
      var sizeCell = e.target.closest('[data-kit-size-cell]');
      if (sizeCell && !sizeCell.disabled) {
        sizeCell.parentElement.querySelectorAll('.is-selected').forEach(function (el) { el.classList.remove('is-selected'); });
        sizeCell.classList.add('is-selected');
        var dlgAtc = kitDlg.querySelector('[data-kit-size-atc]');
        dlgAtc.disabled = false;
        dlgAtc.setAttribute('data-selected-variant', sizeCell.getAttribute('data-variant-id'));
        return;
      }
      var sizeAtc = e.target.closest('[data-kit-size-atc]');
      if (sizeAtc && !sizeAtc.disabled) {
        var vid = sizeAtc.getAttribute('data-selected-variant');
        if (vid) addKit(vid, sizeAtc, kitDlgCtx, closeKitDialog);
        return;
      }
      if (e.target.closest('[data-kit-size-close]')) { closeKitDialog(); return; }
    }

    var scope = e.target.closest('[data-blt-cart-root]');
    if (!scope) { closeSizeMenus(); return; }

    var removeCodeBtn = e.target.closest('[data-remove-code]');
    if (removeCodeBtn) { e.preventDefault(); removeCode(removeCodeBtn.getAttribute('data-remove-code')); return; }

    var couponToggle = e.target.closest('[data-coupon-toggle]');
    if (couponToggle) {
      e.preventDefault();
      couponOpen = true;
      var wrap = couponToggle.closest('[data-coupon-wrap]');
      var form = wrap && wrap.querySelector('[data-coupon-form]');
      couponToggle.hidden = true;
      if (form) {
        form.hidden = false;
        var inp = form.querySelector('[data-coupon-input]');
        if (inp) requestAnimationFrame(function () { inp.focus(); });
      }
      return;
    }

    var checkoutBtn = e.target.closest('[data-checkout-btn]');
    if (checkoutBtn) { checkoutTap(e); return; }

    var kitRemove = e.target.closest('[data-kit-remove]');
    if (kitRemove && !kitRemove.disabled) {
      e.preventDefault();
      var lk = kitRemove.getAttribute('data-line-key');
      if (lk) change(lk, 0, null);
      return;
    }

    var addBtn = e.target.closest('[data-kit-add]');
    if (addBtn && !addBtn.disabled) {
      e.preventDefault();
      // sized products open the picker (no pre-selected variant); single-variant products add directly
      if (addBtn.hasAttribute('data-kit-variants')) openKitDialog(addBtn, scope.getAttribute('data-context'));
      else addKit(addBtn.getAttribute('data-kit-variant'), addBtn, scope.getAttribute('data-context'));
      return;
    }

    var sizeToggle = e.target.closest('[data-size-toggle]');
    if (sizeToggle) {
      e.preventDefault();
      var picker = sizeToggle.closest('[data-size-picker]');
      var menu = picker && picker.querySelector('.blt-cr-size__menu');
      closeSizeMenus(picker);
      if (menu) {
        menu.hidden = !menu.hidden;
        sizeToggle.setAttribute('aria-expanded', menu.hidden ? 'false' : 'true');
      }
      return;
    }
    var sizeOpt = e.target.closest('[data-size-opt]');
    if (sizeOpt && !sizeOpt.disabled) {
      e.preventDefault();
      var li0 = sizeOpt.closest('[data-line-key]');
      var newVid = sizeOpt.getAttribute('data-variant-id');
      closeSizeMenus();
      if (li0 && newVid && newVid !== li0.getAttribute('data-variant-id')) swapVariant(li0, newVid);
      return;
    }
    if (!e.target.closest('[data-size-picker]')) closeSizeMenus();

    var li = e.target.closest('[data-line-key]');
    if (!li) return;
    var key = li.getAttribute('data-line-key');
    var qtyEl = li.querySelector('[data-qty]');
    var qty = parseInt(qtyEl ? qtyEl.textContent : '1', 10) || 1;
    if (e.target.closest('[data-qty-up]')) change(key, qty + 1, li);
    else if (e.target.closest('[data-qty-down]')) change(key, Math.max(0, qty - 1), li);
    else if (e.target.closest('[data-remove]')) change(key, 0, li);
  });

  document.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-coupon-form]');
    if (!form) return;
    e.preventDefault();
    var input = form.querySelector('[data-coupon-input]');
    var code = ((input && input.value) || '').trim();
    if (code) applyCode(code, form);
  });
  document.addEventListener('input', function (e) {
    if (e.target && e.target.matches && e.target.matches('[data-coupon-input]')) clearCouponError();
  });

  document.addEventListener('keydown', function (e) {
    // the size picker sits above the drawer — Escape/Tab belong to it while it's open
    if (kitDialogOpen()) {
      if (e.key === 'Escape') { closeKitDialog(); return; }
      if (e.key === 'Tab') {
        var df = Array.prototype.slice.call(kitDlg.querySelectorAll('button:not([disabled])'));
        if (!df.length) return;
        var dfirst = df[0], dlast = df[df.length - 1];
        if (e.shiftKey && document.activeElement === dfirst) { e.preventDefault(); dlast.focus(); }
        else if (!e.shiftKey && document.activeElement === dlast) { e.preventDefault(); dfirst.focus(); }
      }
      return;
    }
    if (e.key === 'Escape') {
      var openMenu = document.querySelector('[data-size-picker] .blt-cr-size__menu:not([hidden])');
      if (openMenu) { closeSizeMenus(); return; }
    }
    if (!drawer || !drawer.classList.contains('is-open')) return;
    if (e.key === 'Escape') { closeDrawer(); return; }
    if (e.key === 'Tab') {
      var f = focusables();
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  // Auto-open + refresh when the cart changes elsewhere (e.g. PDP add-to-cart dispatches cart:update).
  document.addEventListener('cart:update', function (ev) {
    var src = ev && ev.detail && ev.detail.data && ev.detail.data.source;
    if (src === 'blt-cart') return; // our own change — already swapped
    openDrawer();
    refreshDrawer(false);
  });

  afterRender();
  // the standalone /cart page has no "drawer open" moment — run the stock
  // guard's first check on load (Figma 2763:1023: "re-validate on cart focus")
  if (roots().some(function (r) { return r.getAttribute('data-context') === 'page'; })) {
    validateStockThrottled();
  }
})();
